import { Controller } from "@hotwired/stimulus"
import { reactor } from "../../lib/reactor"

// Survives a Stimulus reconnect. Turbo morphs the 422 HTML and will strip the
// iframe unless we skip that host; Cloudflare still holds this id until remove(),
// even after a morph replaced the host (legacy forms get a random id).
let widgetId = null
let widgetHost = null

export default class extends Controller {
  connect() {
    reactor(this)
    this.stopWaiting = false
    document.addEventListener("turbo:before-morph-element", this.preserveHost)
    this.element.addEventListener("turbo:submit-end", this.onSubmitEnd)
    this.waitForTurnstile(() => this.scheduleRenew())
  }

  disconnect() {
    this.stopWaiting = true
    clearTimeout(this.waitTimer)
    cancelAnimationFrame(this.renewTimer)
    document.removeEventListener("turbo:before-morph-element", this.preserveHost)
    this.element.removeEventListener("turbo:submit-end", this.onSubmitEnd)
  }

  morph() {
    this.scheduleRenew()
  }

  onSubmitEnd = (event) => {
    if (event.detail.success) return
    this.scheduleRenew()
    // form--spinner restores the pre-submit disabled state on this same event.
    setTimeout(() => this.gateSubmit(), 0)
  }

  preserveHost = (event) => {
    const host = this.captchaTarget
    if (host == null) return
    if (event.target === host || host.contains(event.target)) {
      event.preventDefault()
    }
  }

  get captchaTarget() {
    const nested = this.element.querySelector('[data-sitekey]');
    if (nested) return nested;
    if (this.element.id === "") return null;
    return document.querySelector(`[data-turnstile-for="${this.element.id}"]`);
  }

  get submitTarget() {
    return this.element.querySelector('button[type="submit"]');
  }

  // The widget is a cross-origin iframe, so a `dark:` utility cannot reach it.
  get theme() {
    const root = document.documentElement
    if (root.classList.contains('dark')) return 'dark';
    if (root.classList.contains('light')) return 'light';
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  }

  scheduleRenew() {
    cancelAnimationFrame(this.renewTimer)
    this.renewTimer = requestAnimationFrame(() => this.renewChallenge())
  }

  renewChallenge() {
    if (this.captchaTarget == null || this.submitTarget == null) return
    if (typeof window.turnstile === "undefined") return

    this.syncToken("")
    this.gateSubmit()

    if (this.widgetPresent()) {
      try {
        widgetId != null ? turnstile.reset(widgetId) : turnstile.reset()
        return
      } catch (_) { /* remount */ }
    }

    this.initializeTurnstile()
  }

  // The checkbox iframe lives in a closed shadow root, so querySelector misses it.
  widgetPresent() {
    if (typeof window.turnstile === "undefined") return false
    if (widgetHost !== this.captchaTarget) return false
    try {
      widgetId != null ? turnstile.isExpired(widgetId) : turnstile.isExpired()
      return true
    } catch (_) {
      return false
    }
  }

  initializeTurnstile() {
    this.teardownWidget()
    this.captchaTarget.replaceChildren()

    widgetHost = this.captchaTarget
    widgetId = turnstile.render(this.captchaTarget, {
      theme: this.theme,
      'response-field': false,
      callback: (token) => {
        this.syncToken(token);
        this.submitTarget.removeAttribute('disabled');
      }
    });
  }

  teardownWidget() {
    if (typeof window.turnstile === "undefined") return
    if (widgetId != null) {
      try { turnstile.remove(widgetId); } catch (_) { /* already gone */ }
      widgetId = null
    }
    if (this.captchaTarget != null) {
      try { turnstile.remove(this.captchaTarget); } catch (_) { /* already gone */ }
    }
  }

  gateSubmit() {
    this.submitTarget?.setAttribute('disabled', 'disabled')
  }

  syncToken(token) {
    let field = this.element.querySelector('[name="cf-turnstile-response"]');
    if (field == null) {
      field = document.createElement('input');
      field.type = 'hidden';
      field.name = 'cf-turnstile-response';
      this.element.appendChild(field);
    }
    field.value = token;
  }

  waitForTurnstile(callback) {
    if (this.stopWaiting) return
    if (typeof window.turnstile === 'undefined' || this.captchaTarget == null) {
      this.waitTimer = setTimeout(() => this.waitForTurnstile(callback), 100);
    } else {
      callback();
    }
  }
}
